import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Car, 
  Users, 
  Shield, 
  Clock,
  MapPin,
  Wifi,
  Star,
  CheckCircle,
  Plane,
  Sparkles,
  Zap,
  Phone,
  ArrowRight,
  Timer,
  Route
} from "lucide-react";
import { useVehicleImages } from "@/hooks/useVehicleImages";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import strLogo from "@/assets/str-logo.png";
import { Link } from "react-router-dom";

const Transfers = () => {
  const { vehicleImages, loading } = useVehicleImages();
  const { getConfig } = useSiteConfig();

  const transferTypes = [
    {
      title: "Airport Transfers",
      description: "Reliable pickup and drop-off from/to all Dominican airports",
      icon: Plane,
      color: "from-primary to-blue-600",
      routes: [
        "Punta Cana Airport (PUJ)",
        "La Romana Airport (LRM)",
        "Santo Domingo (SDQ)",
        "Puerto Plata (POP)"
      ]
    },
    {
      title: "Hotel Transfers",
      description: "Seamless transportation between hotels and destinations",
      icon: MapPin,
      color: "from-secondary to-red-500",
      routes: [
        "Hotel to Hotel",
        "Resort Transfers",
        "Shopping Centers",
        "Restaurants & Nightlife"
      ]
    },
    {
      title: "Private Transportation",
      description: "Exclusive vehicle and driver at your service",
      icon: Car,
      color: "from-tropical to-yellow-500",
      routes: [
        "Custom Itineraries",
        "Multi-Stop Routes",
        "Wait Time Included",
        "Flexible Scheduling"
      ]
    },
    {
      title: "Group Transportation",
      description: "Perfect for large groups and special events",
      icon: Users,
      color: "from-palm to-green-500",
      routes: [
        "Wedding Parties",
        "Corporate Events",
        "Family Reunions",
        "Tour Groups"
      ]
    }
  ];

  const features = [
    { icon: Shield, title: "Safe & Insured", description: "All vehicles fully insured and regularly inspected", color: "from-primary to-blue-600" },
    { icon: Clock, title: "Always On Time", description: "Punctual pickups with real-time flight monitoring", color: "from-secondary to-red-500" },
    { icon: Users, title: "Pro Drivers", description: "Licensed, bilingual drivers with local expertise", color: "from-tropical to-yellow-500" },
    { icon: Wifi, title: "Premium Comfort", description: "AC, WiFi, refreshments & modern amenities", color: "from-palm to-green-500" }
  ];

  const popularRoutes = [
    { route: "Airport ↔ Bavaro", time: "45 min", distance: "25 km", price: "From $35" },
    { route: "Airport ↔ Cap Cana", time: "30 min", distance: "15 km", price: "From $40" },
    { route: "Airport ↔ Uvero Alto", time: "60 min", distance: "35 km", price: "From $45" },
    { route: "Airport ↔ La Romana", time: "90 min", distance: "60 km", price: "From $65" }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-secondary"></div>
        
        {/* Animated Blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-tropical/30 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-palm/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        
        {/* Floating Icons */}
        <div className="absolute top-1/4 left-1/4 animate-float">
          <Car className="w-10 h-10 text-white/30" />
        </div>
        <div className="absolute bottom-1/3 right-1/4 animate-float animation-delay-2000">
          <Plane className="w-8 h-8 text-tropical/50" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-float animation-delay-4000">
          <MapPin className="w-6 h-6 text-white/40" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            {/* Logo */}
            <div className="mb-8 flex justify-center animate-fade-in-up">
              <img 
                src={strLogo} 
                alt="Servicio Turístico Rachel" 
                className="h-20 w-auto drop-shadow-2xl"
              />
            </div>

            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 text-lg py-2 px-6 animate-fade-in-up animation-delay-200">
              <Car className="w-4 h-4 mr-2" />
              Premium Transfer Services
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-400">
              <span className="block">Reliable</span>
              <span className="bg-gradient-to-r from-tropical via-white to-palm bg-clip-text text-transparent">
                Transportation
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-10 animate-fade-in-up animation-delay-600">
              Premium transfer services throughout Dominican Republic. Airport pickups, 
              hotel transfers, and custom transportation—always comfortable, safe, and on time.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-800">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                <Zap className="mr-2 h-5 w-5" />
                Get Instant Quote
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 hover:scale-105 transition-all">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Vehicle Fleet Section */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0 text-base py-2 px-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Our Fleet
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Choose Your</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Perfect Vehicle</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From intimate couples' transfers to large group transportation, we have the ideal vehicle for every journey
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
            </div>
          ) : vehicleImages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No vehicles available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {vehicleImages
                .sort((a, b) => a.min_passengers - b.min_passengers)
                .map((vehicle, index) => (
                <Card 
                  key={vehicle.id} 
                  className={`group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 animate-fade-in-up ${index === 1 ? 'ring-2 ring-secondary shadow-secondary/20' : ''}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Popular Badge */}
                  {index === 1 && (
                    <div className="absolute top-4 right-4 z-20">
                      <Badge className="bg-gradient-to-r from-secondary to-red-500 text-white border-0 shadow-lg">
                        <Star className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )}

                  {/* Vehicle Image */}
                  <div className="relative h-48 overflow-hidden">
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                      style={{ backgroundImage: `url(${vehicle.image_url})` }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    
                    {/* Service Type Badge */}
                    <div className="absolute bottom-4 left-4">
                      <Badge className={`${vehicle.service_type === 'vip' ? 'bg-gradient-to-r from-tropical to-yellow-500' : 'bg-gradient-to-r from-primary to-blue-600'} text-white border-0`}>
                        {vehicle.service_type === 'vip' ? '⭐ VIP Service' : 'Standard Service'}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl">{vehicle.vehicle_name}</CardTitle>
                    <CardDescription>
                      {vehicle.description || 'Professional transfer service'}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {/* Capacity */}
                    <div className="flex items-center justify-between mb-4 p-3 bg-muted/50 rounded-lg">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Capacity
                      </span>
                      <span className="font-bold text-primary">{vehicle.min_passengers}-{vehicle.max_passengers} pax</span>
                    </div>

                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {vehicle.features && vehicle.features.length > 0 ? (
                        vehicle.features.slice(0, 3).map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-palm flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-palm flex-shrink-0" />
                            <span className="text-muted-foreground">Air Conditioning</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-palm flex-shrink-0" />
                            <span className="text-muted-foreground">Professional Driver</span>
                          </div>
                        </>
                      )}
                    </div>

                    <Button 
                      className={`w-full group/btn ${index === 1 
                        ? 'bg-gradient-to-r from-secondary to-red-500 hover:from-secondary/90 hover:to-red-500/90' 
                        : 'bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90'
                      } text-white shadow-lg hover:shadow-xl transition-all`}
                    >
                      Book Now
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Transfer Types Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-tropical to-palm text-white border-0 text-base py-2 px-6">
              <Route className="w-4 h-4 mr-2" />
              Service Types
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Complete Transfer</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Solutions</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From simple airport pickups to complex multi-destination transportation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {transferTypes.map((type, index) => (
              <Card 
                key={index} 
                className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Gradient Top Border */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${type.color}`}></div>
                
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${type.color} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <type.icon className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-1">{type.title}</CardTitle>
                      <CardDescription className="text-base">
                        {type.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {type.routes.map((route, routeIndex) => (
                      <div key={routeIndex} className="flex items-center gap-2 text-sm group/item">
                        <CheckCircle className="h-4 w-4 text-palm flex-shrink-0 group-hover/item:scale-110 transition-transform" />
                        <span className="text-muted-foreground">{route}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes Section */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-blue-600 text-white border-0 text-base py-2 px-6">
              <MapPin className="w-4 h-4 mr-2" />
              Popular Routes
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Most Requested</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Destinations</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Quick reference for our most popular transfer routes and estimated pricing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularRoutes.map((route, index) => (
              <Card 
                key={index} 
                className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Gradient Glow on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 mx-auto bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Route className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">{route.route}</CardTitle>
                  <div className="text-3xl font-bold bg-gradient-to-r from-tropical to-palm bg-clip-text text-transparent">
                    {route.price}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Timer className="w-4 h-4" />
                      <span>{route.time}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{route.distance}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-secondary to-red-500 text-white border-0 text-base py-2 px-6">
              <Star className="w-4 h-4 mr-2" />
              Why Choose STR
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Premium Service</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Standards</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every detail designed for your comfort and peace of mind
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group text-center border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className={`w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <feature.icon className="h-10 w-10 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-10 left-10 animate-float">
          <Sparkles className="w-12 h-12 text-white/30" />
        </div>
        <div className="absolute bottom-10 right-10 animate-float animation-delay-2000">
          <Car className="w-10 h-10 text-tropical/50" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Book Your Transfer
              <span className="block text-tropical">Today!</span>
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90 animate-fade-in-up animation-delay-200">
              Secure, comfortable, and reliable transportation is just a booking away. 
              Contact us for instant quotes and reservations.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-4 mb-10 animate-fade-in-up animation-delay-400">
              {[
                { icon: Clock, text: "24/7 Availability" },
                { icon: Shield, text: "Fully Insured" },
                { icon: Star, text: "5-Star Service" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <item.icon className="w-4 h-4" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-600">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                Get Instant Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-10 py-6 hover:scale-105 transition-all"
                onClick={() => window.open(`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`, '_self')}
              >
                <Phone className="mr-2 h-5 w-5" />
                {getConfig('contact_phone', '+1 (809) 123-4567')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Transfers;
